
import gql from 'graphql-tag';
import productPromotionFragment from '~/apollo/fragments/productPromotionFragment';
import wlciPlanScenariosQuery from '~/apollo/queries/product/WLCIPlanScenario.gql';
import wlciTooltips from '~/apollo/queries/product/tooltips/wlciTooltips';
import commonProductDetailMixins from '~/mixins/commonProductDetailMixins.js';
import productPromotionMixins from '~/mixins/productPromotionMixins.js';
import productShortcutMixins from "~/mixins/productShortcutMixins.js";
import productInsuranceNameMixins from '~/mixins/productInsuranceNameMixins';

export default {
  mixins: [commonProductDetailMixins, productPromotionMixins, productShortcutMixins, productInsuranceNameMixins],

  data() {
    return {
      wlciScenarios: null,
      loading: 0,
      currentLocale: this.$normalizeLocale(this.$i18n.locale),
    };
  },
  // page component definitions
  apollo: {
    $loadingKey: 'loading',
    wlciScenarios: {
      query: gql`
        ${wlciPlanScenariosQuery}
        ${productPromotionFragment}
      `,
      prefetch: true,
      variables() {
        return {
          scenario_code: this.$route.params.slug,
          locale: this.currentLocale,
        };
      },
    },
    wlciTooltips: {
      query: wlciTooltips,
      prefetch: true,
      variables() {
        return { currentlocale: this.$normalizeLocale(this.$i18n.locale) };
      },
      update(data) {
        return this.$normalizeStrapi(data.infoTooltip.data);
      },
    },
  },
  computed: {
    simpleItems() {
      if (this.wlciScenarios?.data[0]) {
        const item = this.wlciScenarios.data[0];
        let promotionPrices = Array;
        promotionPrices = [];

        const planName = item.attributes.wlci_plan.data.attributes.wlci_plan_translations.data[0].attributes.name;
        const insurer = item.attributes.wlci_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.insurance_company_translations.data[0].attributes.name;
        let promotions = null;
        let promotion = null;
        let promotion_last_call = null;

        let decoderCTALink = this.$generateWhatsappCTALink(this.$t('products.wlci.wlci_categoryName'), insurer, planName);
        let decoderCTAText = '';

        const shortcut = this.getProductShortcut(item.attributes);

        // check scenario level promo first , then plan level promotion
        if (item.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.product_promotions.data;
        }
        if (item.attributes.wlci_plan.data.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.wlci_plan.data.attributes.product_promotions.data;
        }

        if(promotions != null) {
          promotion = this.checkPromotionValid(promotions);
          promotion_last_call = this.checkPromotionLastCall(promotion);

          if (!promotion) {
            promotion = null;
          } else if (promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink) {
            decoderCTALink = promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink;
            decoderCTAText = this.$t('products.product_website');
          }
        }

        const filteredTranslations = this.getFilteredTranslations(item.attributes.wlci_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
        .insurance_company_translations.data[0].attributes.localizations.data);
        // console.debug(filteredTranslations);

        return {
          scenario_code: item.attributes.scenario_code,
          payment_term: item.attributes.payment_term,
          annual_premium: item.attributes.annual_premium,
          issue_age_from: item.attributes.issue_age_from,
          issue_age_to: item.attributes.issue_age_to,
          currency: item.attributes.currency.data.attributes.code,

          g_return_ratio_10: item.attributes.g_return_ratio_10,
          g_return_ratio_20: item.attributes.g_return_ratio_20,
          g_return_ratio_30: item.attributes.g_return_ratio_30,

          p_return_ratio_10: item.attributes.p_return_ratio_10,
          p_return_ratio_20: item.attributes.p_return_ratio_20,
          p_return_ratio_30: item.attributes.p_return_ratio_30,

          // plan
          coverage_score: item.attributes.wlci_plan.data.attributes.coverage_score,
          terms_score: item.attributes.wlci_plan.data.attributes.terms_score,
          no_maj_ci: item.attributes.wlci_plan.data.attributes.no_maj_ci,
          no_early_ci: item.attributes.wlci_plan.data.attributes.no_early_ci,
          death_benefit: item.attributes.wlci_plan.data.attributes.death_benefit,
          max_payout: item.attributes.wlci_plan.data.attributes.max_payout,
          waiting_period: item.attributes.wlci_plan.data.attributes.waiting_period,
          single_ci_score: item.attributes.wlci_plan.data.attributes.single_ci_score,
          multi_ci_score: item.attributes.wlci_plan.data.attributes.multi_ci_score,
          overall_score: item.attributes.wlci_plan.data.attributes.overall_score.toFixed(1),
          overall_star: item.attributes.wlci_plan.data.attributes.overall_star,
          multiple_claim:
            item.attributes.wlci_plan.data.attributes.wlci_multiple_claim.data.attributes
              .wlci_multiple_claim_translations.data[0].attributes.description,

          plan_name: planName,

          insurer_name_first:
            item.attributes.wlci_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .insurance_company_translations.data[0].attributes.name,
          insurer_name_second:
            filteredTranslations[0].attributes.name,
          insurer_logo:
            item.attributes.wlci_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.logo,
          insurer_code:
            item.attributes.wlci_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.code,

          // ***** CI Coverage *****
          coverage_payouts: item.attributes.wlci_plan.data.attributes.wlci_coverage_payouts.data,

          // ***** CI Terms *****
          term_values: item.attributes.wlci_plan.data.attributes.wlci_term_values.data,

          product_promotion: promotion,
          promotion_last_call,

          decoder_cta_link: decoderCTALink,
          decoder_cta_text: decoderCTAText,

          scenarioId: item.id,
          shortcut: shortcut,
        };
      }
      return null;
    },
    product() {
      if (!this.simpleItems) return null;

      return {
        highlightSection: {
          scenarioCode: this.simpleItems.scenario_code,
          scenarioId: this.simpleItems.scenarioId,
          showFiveStarBadge: this.$formatShowFiveStarBadge(this.simpleItems.overall_star),
          productName: this.simpleItems.plan_name,
          insuranceCompany: {
            name: this.simpleItems.insurer_name_first + ' | ' + this.simpleItems.insurer_name_second,
            code: this.simpleItems.insurer_code,
            logoSrc: this.simpleItems.insurer_logo?.data?.attributes?.url,
            logoAlt: this.simpleItems.insurer_name_first,
          },
          userRating: {
            score: this.simpleItems.overall_star,
            numberOfRatedUser: '0',
          },
          tenlifeScore: {
            score: parseFloat(this.simpleItems.overall_score),
            description: this.$t('products.wlci.wlci_overallScore'),
            toolTipHtml: this.getTooltipContentByKey('wlci_overallScore'),
          },
          features: [
            {
              text: this.simpleItems.multiple_claim,
              type: 'multiple_claims',
            },
            {
            text: this.simpleItems.payment_term + this.$t('products.wlci.wlci_yearpayment'),
            type: 'payment_term',
          },
          ],
          keyItems: [
            {
              value: this.$formatScore(this.simpleItems.coverage_score, 1, 'wlci') + ' / 10',
              key: this.$t('products.wlci.wlci_wholeLifeCICoverageScore'),
              topSubText: '',
              toolTipHtml: this.getTooltipContentByKey('wlci_wholeLifeCICoverageScore'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.$formatScore(this.simpleItems.terms_score, 1, 'wlci') + ' / 10',
              key: this.$t('products.wlci.wlci_termsScore'),
              topSubText: '',
              toolTipHtml: this.getTooltipContentByKey('wlci_termsScore'),
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.simpleItems.payment_term,
              key: this.$t('products.wlci.wlci_premiumTermYears'),
              topSubText: '',
              toolTipHtml: '',
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.$formatPrice(this.simpleItems.annual_premium),
              key: this.$t('products.wlci.wlci_annualPremium'),
              topSubText: this.simpleItems.currency,
              toolTipHtml: this.getTooltipContentByKey('wlci_annualPremium'),
              symbolValue: '',
              originalValue: '',
            },
          ],
          mainButtonCTA: {
            text: this.simpleItems.decoder_cta_text,
            link: this.simpleItems.decoder_cta_link,
          },
          showShortcut: this.simpleItems.shortcut,
        },
        detailSection: {
          details: [
            {
              id: 0,
              sectionTitle: this.$t('products.wlci.wlci_overview'),
              keyValues: [
                {
                  key: this.$t('products.wlci.wlci_overallScore'),
                  value: this.$formatScore(parseFloat(this.simpleItems.overall_score), 1, 'wlci')  + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_overallScore'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_wholeLifeCICoverageScore'),
                  value: this.$formatScore(this.simpleItems.coverage_score, 1, 'wlci') + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_wholeLifeCICoverageScore'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_termsScore'),
                  value: this.$formatScore(this.simpleItems.terms_score, 1, 'wlci') + ' / 10',
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_termsScore'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_premiumTermYears'),
                  value: this.simpleItems.payment_term,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_annualPremium'),
                  value: this.simpleItems.currency + ' ' + this.$formatPrice(this.simpleItems.annual_premium),
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_annualPremium'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_numberofCoveredMajorIllnesses'),
                  value: this.simpleItems.no_maj_ci,
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_numberofCoveredMajorIllnesses'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_numberofCoveredEarlyIllnesses'),
                  value: this.simpleItems.no_early_ci,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_deathBenefit'),
                  value: this.simpleItems.death_benefit,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_maximumPayoutBasicSumAssured'),
                  value:
                    this.simpleItems.max_payout === -9.99
                      ? this.$t('products.wlci.wlci_nolimit')
                      : this.$formatPercentage(this.simpleItems.max_payout),
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_maximumPayoutBasicSumAssured'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_waitingPeriod'),
                  value: this.simpleItems.waiting_period + ' ' + this.$t('products.dayUnit'),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_issueAge'),
                  value: this.$formatAgeRange(
                    this.currentLocale,
                    this.simpleItems.issue_age_from,
                    this.simpleItems.issue_age_to,
                    this.$t('products.ageUnit'),
                    this.$t('products.to')
                  ),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
            {
              id: 1,
              sectionTitle: this.$t('products.wlci.wlci_cICoverage'),
              keyValues: [
                {
                  key: this.$t('products.wlci.wlci_wholeLifeCICoverageScore'),
                  value: this.$formatScore(this.simpleItems.coverage_score, 1, 'wlci') + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_singleCICoverageScore'),
                  value: this.$formatScore(this.simpleItems.single_ci_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP001').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP001').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP002').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP002').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP003').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP003').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP004').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP004').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP005').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP005').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T014').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T014').value),
                      keyToolTipHtml: this.getTooltipContentByKey('wlci_noDeductionFromMajorPayout')
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'C001').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'C001').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'C002').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'C002').value),
                    },
                  ],
                },
                {
                  key: this.$t('products.wlci.wlci_multipleCICoverageScore'),
                  value: this.$formatScore(this.simpleItems.multi_ci_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T001').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T001').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T007').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T007').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'C003').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'C003').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP006').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP006').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP007').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP007').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP008').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP008').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP009').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP009').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP010').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP010').value),
                    },
                    {
                      key: this.getCIPayout(this.simpleItems.coverage_payouts, 'CP011').label,
                      value: this.$formatPercentage(this.getCIPayout(this.simpleItems.coverage_payouts, 'CP011').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'C004').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'C004').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'C005').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'C005').value),
                    },
                  ],
                },
              ],
            },
            {
              id: 2,
              sectionTitle: this.$t('products.wlci.wlci_cITerms'),
              keyValues: [
                {
                  key: this.$t('products.wlci.wlci_termsScore'),
                  value: this.$formatScore(this.simpleItems.terms_score, 1, 'wlci') + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_cancerTerms'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T003').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T003').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T004').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T004').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T005').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T005').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T006').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T006').value),
                    },
                  ],
                },
                {
                  key: this.$t('products.wlci.wlci_heartandStrokeTerms'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T008').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T008').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T009').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T009').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T010').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T010').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T011').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T011').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T012').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T012').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T013').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T013').value),
                    },
                  ],
                },
                {
                  key: this.$t('products.wlci.wlci_generalTerms'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T015').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T015').value),
                    },
                    {
                      key: this.getCITerm(this.simpleItems.term_values, 'T016').label,
                      value: this.$formatSymbol(this.getCITerm(this.simpleItems.term_values, 'T016').value),
                    },
                  ],
                },
              ],
            },
            {
              id: 3,
              sectionTitle: this.$t('products.wlci.wlci_return'),
              keyValues: [
                {
                  key: this.$t('products.wlci.wlci_guaranteedReturnRatio'),
                  value: '',
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_guaranteedReturnRatio'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_10thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.g_return_ratio_10),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_20thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.g_return_ratio_20),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_30thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.g_return_ratio_30),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_projectedReturnRatio'),
                  value: '',
                  keyToolTipHtml: this.getTooltipContentByKey('wlci_projectedReturnRatio'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_10thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.p_return_ratio_10),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_20thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.p_return_ratio_20),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.wlci.wlci_30thPolicyYear'),
                  value: this.$formatPercentage(this.simpleItems.p_return_ratio_30),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
          ],
          promotion: {
            category:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionCategory,
            title:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionTitle,
            ctaButtonLink:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonLink,
            ctaButtonText:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonText,
            promotionId: this.simpleItems.product_promotion?.id,
            scenarioId: this.simpleItems.scenarioId,
            lastCall: this.simpleItems.promotion_last_call

          },
        },
      };
    },
  },

  methods: {
    getCIPayout(payouts, code) {
      let tmpPayout = null;
      tmpPayout = payouts.filter((payout) => {
        return code === payout.attributes.wlci_coverage.data.attributes.code;
      });
      return {
        label:
          tmpPayout[0].attributes.wlci_coverage.data.attributes.wlci_coverage_translations.data[0].attributes
            .description,
        value: tmpPayout[0].attributes.c_value,
      };
    },

    getCITerm(terms, code) {
      let tmpTerm = null;
      tmpTerm = terms.filter((term) => {
        return code === term.attributes.wlci_term.data.attributes.code;
      });
      return {
        label: tmpTerm[0].attributes.wlci_term.data.attributes.wlci_term_translations.data[0].attributes.description,
        value: tmpTerm[0].attributes.t_value,
      };
    },

    getCurrency(curr) {
      if (this.currentLocale === 'zh-Hant-HK') {
        return curr.text;
      } else {
        return curr.localizations.data[0].attributes.text;
      }
    },

    getTooltipContentByKey(mKey) {
      return this.wlciTooltips?.WLCI?.ToolTips?.find(({ Key }) => Key === mKey)?.ToolTipContent ?? '';
    },
  },
};
